// UPPDRAG-53 — Swedish public holidays.
//
// Computes the official Swedish "röda dagar" for any year:
//   - 9 fixed-date holidays (Nyårsdagen, Trettondedag jul, 1 maj,
//     Sveriges nationaldag, Julafton, Juldagen, Annandag jul, Nyårsafton)
//   - 5 Easter-relative holidays (Långfredag, Påskdagen, Annandag påsk,
//     Kristi himmelsfärdsdag, Pingstdagen)
//   - 2 movable Midsommar (Midsommarafton + Midsommardagen — Friday/Saturday
//     between June 19-26)
//   - 1 movable Alla helgons dag (Saturday between Oct 31 - Nov 6)
//
// Pure (no I/O), so vitest pins every branch and the date-picker can call
// without any setup.
//
// Easter algorithm: Anonymous Gregorian (Meeus/Jones/Butcher). Standard
// closed-form solution; matches the official Swedish almanac dates 1900-2099.

/** A computed Swedish holiday for a given year. */
export interface SwedishHoliday {
  /** Date in local YYYY-MM-DD form (no time). */
  iso: string;
  /** Swedish display name. */
  name: string;
}

/**
 * Compute the Easter Sunday date (Gregorian) for a given year using the
 * Anonymous Gregorian algorithm. Returns the calendar month + day.
 *
 * Reference: https://en.wikipedia.org/wiki/Date_of_Easter
 */
function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);  // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

/** Format a year/month/day triple as YYYY-MM-DD (local, no TZ shift). */
function iso(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Add `days` to a YYYY-MM-DD date (local). */
function addDaysIso(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  // Build a UTC date to avoid TZ-shift on dates near DST boundaries.
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return iso(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

/**
 * Returns the date (in YYYY-MM-DD form) of the first weekday `dow` in the
 * given range (`startMonth`/`startDay` inclusive, `endMonth`/`endDay` inclusive).
 * `dow`: 0=Sun … 6=Sat.
 */
function findWeekdayInRange(
  year: number,
  startMonth: number, startDay: number,
  endMonth: number, endDay: number,
  dow: number,
): string {
  const start = new Date(Date.UTC(year, startMonth - 1, startDay));
  const end = new Date(Date.UTC(year, endMonth - 1, endDay));
  for (let d = start; d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    if (d.getUTCDay() === dow) {
      return iso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
    }
  }
  // Should never happen given the ranges below cover ≥ 7 days.
  throw new Error(`UPPDRAG-53: no weekday ${dow} found in range`);
}

/**
 * All Swedish "röda dagar" (public holidays) for the given year, plus
 * Midsommarafton + Julafton + Nyårsafton (technically "de facto" red days
 * — most workplaces treat them as half-day or full holiday).
 *
 * Returned list is sorted by date.
 */
export function getSwedishHolidays(year: number): SwedishHoliday[] {
  const easter = easterSunday(year);
  const easterIso = iso(year, easter.month, easter.day);

  // Easter-relative
  const langfredag = addDaysIso(easterIso, -2);  // Good Friday
  const paskdagen = easterIso;                   // Easter Sunday
  const annandagPask = addDaysIso(easterIso, 1);
  const kristiHim = addDaysIso(easterIso, 39);   // Ascension (always Thursday)
  const pingstdagen = addDaysIso(easterIso, 49); // Pentecost

  // Midsommarafton: Friday between June 19-26 inclusive.
  // Midsommardagen: Saturday after Midsommarafton.
  const midsommarafton = findWeekdayInRange(year, 6, 19, 6, 26, 5);
  const midsommardagen = addDaysIso(midsommarafton, 1);

  // Alla helgons dag: Saturday between Oct 31 and Nov 6 inclusive.
  const allaHelgon = findWeekdayInRange(year, 10, 31, 11, 6, 6);

  const list: SwedishHoliday[] = [
    { iso: iso(year, 1, 1), name: "Nyårsdagen" },
    { iso: iso(year, 1, 6), name: "Trettondedag jul" },
    { iso: langfredag, name: "Långfredagen" },
    { iso: paskdagen, name: "Påskdagen" },
    { iso: annandagPask, name: "Annandag påsk" },
    { iso: iso(year, 5, 1), name: "Första maj" },
    { iso: kristiHim, name: "Kristi himmelsfärdsdag" },
    { iso: pingstdagen, name: "Pingstdagen" },
    { iso: iso(year, 6, 6), name: "Sveriges nationaldag" },
    { iso: midsommarafton, name: "Midsommarafton" },
    { iso: midsommardagen, name: "Midsommardagen" },
    { iso: allaHelgon, name: "Alla helgons dag" },
    { iso: iso(year, 12, 24), name: "Julafton" },
    { iso: iso(year, 12, 25), name: "Juldagen" },
    { iso: iso(year, 12, 26), name: "Annandag jul" },
    { iso: iso(year, 12, 31), name: "Nyårsafton" },
  ];

  return list.sort((a, b) => (a.iso < b.iso ? -1 : a.iso > b.iso ? 1 : 0));
}

/**
 * Lookup table builder — useful for date-picker modifiers that ask
 * "is this Date a holiday and what's its name".
 *
 * Returns Map<YYYY-MM-DD, holiday-name>.
 */
export function getSwedishHolidaysMap(year: number): Map<string, string> {
  const out = new Map<string, string>();
  for (const h of getSwedishHolidays(year)) {
    out.set(h.iso, h.name);
  }
  return out;
}

/**
 * Convenience: returns the holiday name for a given Date object, or null
 * if the date is not a Swedish holiday. Uses the local-TZ year/month/day
 * to match how date-pickers display dates.
 */
export function getSwedishHolidayName(date: Date): string | null {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();
  const key = iso(y, m, d);
  const map = getSwedishHolidaysMap(y);
  return map.get(key) ?? null;
}

/**
 * Convenience: true if the date is a Swedish holiday.
 */
export function isSwedishHoliday(date: Date): boolean {
  return getSwedishHolidayName(date) !== null;
}
